import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import ModuleLocked from './pages/ModuleLocked';
import { useEntitlements } from './services/entitlements';
import { usePermissions } from './services/permissions';
import ReadOnlyBanner from './components/ReadOnlyBanner';
import PaymentDueBanner from './components/PaymentDueBanner';
import Footer from './components/Footer';
import Layout from './components/Layout';
import { AppTitle } from './components/AppTitle';
import { ThemeProvider } from './contexts/ThemeContext';
import { OrgFilterProvider } from './contexts/OrgFilterContext';
// Page components are route-split: each is React.lazy so its code lands in a
// separate chunk fetched on first navigation, keeping the initial bundle small.
// Static imports above (hooks, Layout/guards, banners, ModuleLocked, providers)
// are deliberately NOT lazy — they render outside <Routes> or inside the guards.
const DocumentProcessor = React.lazy(() => import('./pages/DocumentProcessor'));
const Login = React.lazy(() => import('./pages/Login'));
const PortalLogin = React.lazy(() => import('./pages/PortalLogin'));
const PortalPage = React.lazy(() => import('./pages/PortalPage'));
const GuestTaskPage = React.lazy(() => import('./pages/GuestTaskPage'));
const DossierPortalPage = React.lazy(() => import('./pages/DossierPortalPage'));
const UserDashboard = React.lazy(() => import('./pages/UserDashboard'));
const CatalogPage = React.lazy(() => import('./pages/CatalogPage'));
const RecordDetailPage = React.lazy(() => import('./pages/RecordDetailPage'));
const DossierDetailPage = React.lazy(() => import('./pages/DossierDetailPage'));
const DossiersListPage = React.lazy(() => import('./pages/DossiersListPage'));
const SubmissionsListPage = React.lazy(() => import('./pages/SubmissionsListPage'));
const SubmissionDetailPage = React.lazy(() => import('./pages/SubmissionDetailPage'));
const RecordTypeBuilderPage = React.lazy(() => import('./pages/RecordTypeBuilderPage'));
const EngagementsPage = React.lazy(() => import('./pages/EngagementsPage'));
const WorkTypeBuilderPage = React.lazy(() => import('./pages/WorkTypeBuilderPage'));
const SignUp = React.lazy(() => import('./pages/SignUp').then(m => ({ default: m.SignUp })));
const ImpersonateEntry = React.lazy(() => import('./pages/ImpersonateEntry'));
const PendingApproval = React.lazy(() => import('./pages/PendingApproval'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const TaskDetailPage = React.lazy(() => import('./pages/TaskDetailPage'));
const TimesheetDetailPage = React.lazy(() => import('./pages/TimesheetDetailPage'));
const TimesheetsReportPage = React.lazy(() => import('./pages/TimesheetsReportPage'));
const MasterDataPage = React.lazy(() => import('./pages/MasterDataPage'));
const WhatsAppSendersPage = React.lazy(() => import('./pages/WhatsAppSendersPage'));
const ExternalMasterPage = React.lazy(() => import('./pages/ExternalMasterPage'));
const ApprovalsPage = React.lazy(() => import('./pages/ApprovalsPage'));
const AgreementsPage = React.lazy(() => import('./pages/AgreementsPage'));
const CompliancePage = React.lazy(() => import('./pages/CompliancePage'));
const DepartmentsAdminPage = React.lazy(() => import('./pages/DepartmentsAdminPage'));
const InsurancesPage = React.lazy(() => import('./pages/InsurancesPage'));
const LicensesPage = React.lazy(() => import('./pages/LicensesPage'));
const OrganizationsAdminPage = React.lazy(() => import('./pages/OrganizationsAdminPage'));
const PropertyLocationsPage = React.lazy(() => import('./pages/PropertyLocationsPage'));
const RealEstatePage = React.lazy(() => import('./pages/RealEstatePage'));
const RecycleBinPage = React.lazy(() => import('./pages/RecycleBinPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const DashboardHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.DashboardHub })));
const DocumentsHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.DocumentsHub })));
const TasksHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.TasksHub })));
const TimesheetHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.TimesheetHub })));
const RecordsHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.RecordsHub })));
const OrganizationHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.OrganizationHub })));
const AutomationHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.AutomationHub })));
const PlatformHub = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.PlatformHub })));
const ToSection = React.lazy(() => import('./pages/Hubs').then(m => ({ default: m.ToSection })));
const InsuranceReportPage = React.lazy(() => import('./pages/InsuranceReportPage'));
const LicenseReportPage = React.lazy(() => import('./pages/LicenseReportPage'));
const InsuranceDetailPage = React.lazy(() => import('./pages/InsuranceDetailPage'));
const LicenseDetailPage = React.lazy(() => import('./pages/LicenseDetailPage'));
const AgreementDetailPage = React.lazy(() => import('./pages/AgreementDetailPage'));
const AgreementReportPage = React.lazy(() => import('./pages/AgreementReportPage'));
const RealEstateReportPage = React.lazy(() => import('./pages/RealEstateReportPage'));
const DocumentsReportPage = React.lazy(() => import('./pages/DocumentsReportPage'));
const TasksReportPage = React.lazy(() => import('./pages/TasksReportPage'));
const ComplianceReportPage = React.lazy(() => import('./pages/ComplianceReportPage'));
const PropertyDetailPage = React.lazy(() => import('./pages/PropertyDetailPage'));
const DocumentSearchPage = React.lazy(() => import('./pages/DocumentSearchPage'));
const ConfigurationsPage = React.lazy(() => import('./pages/ConfigurationsPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const ZohoReviewPage = React.lazy(() => import('./pages/ZohoReviewPage'));

/**
 * Protected route that only checks auth. Used for pages that still have their
 * own layouts (Admin, DocumentProcessor, TaskDetail).
 * Footer is added below their content for brand consistency.
 */
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div style={{ flex: 1 }}>{children}</div>
      <Footer />
    </div>
  );
};

/**
 * Protected route that wraps the page in the shared PaperBridge shell
 * (Sidebar + Header + Footer). Used for pages that participate in the
 * unified layout system.
 */
const ProtectedLayoutRoute = ({ children }: { children: React.ReactElement }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <Layout>{children}</Layout>;
};

/**
 * Route that requires super_admin specifically. Used by Field Definitions
 * and Document Types since 2026-05-31 — those screens edit platform-wide
 * configuration and are also editable from the Site Panel by super_admin.
 *
 * Anyone authenticated but not super_admin sees an in-shell Access
 * Restricted card instead of the page, so URL-typing or stale bookmarks
 * don't surface the editor. The sidebar entries hide for the same set,
 * but defence in depth: gate both surfaces.
 */
const SuperAdminLayoutRoute = ({ children, what }: { children: React.ReactElement; what?: string }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  const userStr = localStorage.getItem('user');
  const role = (() => { try { return userStr ? JSON.parse(userStr).role : null; } catch { return null; } })();
  if (role !== 'super_admin') {
    return (
      <Layout>
        <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
          <div style={{
            background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '20px',
            padding: '32px 28px', maxWidth: '480px', textAlign: 'center',
            fontFamily: "'Poppins', sans-serif",
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 600, color: '#111827' }}>
              Access Restricted
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
              {/* This guard covers five different pages, but the copy named only
                  the two it was written for — so a tenant admin opening Roles was
                  told about field definitions. Each route says what it is. */}
              {what || 'This page'} is platform-wide configuration managed by the
              super admin. If you need a change here, contact your super admin.
            </p>
          </div>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
};

/**
 * Route that requires tenant_admin or super_admin — the "Settings" surfaces
 * (users, orgs, departments, approval matrix, SLA/storage/Zoho config). Hidden
 * in the sidebar for non-admins; this blocks direct-URL access too, so an
 * Accountant/Reviewer typing /sla-config sees an Access Restricted card rather
 * than the editor. Backend write endpoints are also require_admin — defence in depth.
 */
const AdminLayoutRoute = ({ module, perm, children }: { module?: string; perm?: string; children: React.ReactElement }) => {
  const token = localStorage.getItem('token');
  const { ent, hasModule } = useEntitlements();
  const { can, loading: permsLoading } = usePermissions();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  const userStr = localStorage.getItem('user');
  const role = (() => { try { return userStr ? JSON.parse(userStr).role : null; } catch { return null; } })();
  // Built-in admins always pass. A custom role passes when it holds the page's
  // specific permission (perm) — the generic model: tick manage_users on any
  // role and its users reach /users, no role-name special-casing. Pages with no
  // clean permission (Configurations, Subscription) pass `perm` undefined and
  // stay admin-only. Fail closed: wait for perms to load before deciding.
  const isBuiltinAdmin = role === 'super_admin' || role === 'tenant_admin';
  if (!isBuiltinAdmin && perm && permsLoading) {
    return <Layout><div style={{ padding: '60px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>Loading…</div></Layout>;
  }
  const allowed = isBuiltinAdmin || (perm ? can(perm) : false);
  if (!allowed) {
    return (
      <Layout>
        <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
          <div style={{
            background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '20px',
            padding: '32px 28px', maxWidth: '480px', textAlign: 'center',
            fontFamily: "'Poppins', sans-serif",
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 600, color: '#111827' }}>
              Access Restricted
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
              This is an administrator settings page. If you need a change here,
              contact your workspace administrator.
            </p>
          </div>
        </div>
      </Layout>
    );
  }
  // Optional plan-module gate (e.g. Team Performance needs mod_tasks_compliance).
  if (module && !hasModule(module)) {
    return <Layout><ModuleLocked module={module} available={ent?.modules || []} /></Layout>;
  }
  return <Layout>{children}</Layout>;
};

/**
 * Route wrapped in the shell that also requires a plan module. If the tenant's
 * plan excludes it, an in-shell "not in your plan" card renders instead of the
 * page — defence in depth alongside the hidden sidebar nav and the backend 403.
 * Fails open while entitlements load; super_admin always passes.
 */
// `module` gates on a single module. `anyOf` gates a cross-module page on holding
// ANY of several modules (e.g. Acknowledgements aggregates Real Estate, Agreements,
// Insurance and Licenses — a user with any one of them must be able to open it).
const ModuleLayoutRoute = ({ module, anyOf, perm, children }: { module?: string; anyOf?: string[]; perm?: string; children: React.ReactElement }) => {
  const token = localStorage.getItem('token');
  const { ent, hasModule, loading } = useEntitlements();
  const { can, loading: permsLoading } = usePermissions();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '60px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>Loading…</div>
      </Layout>
    );
  }
  const allowed = anyOf && anyOf.length ? anyOf.some((m) => hasModule(m)) : (module ? hasModule(module) : true);
  if (!allowed) {
    return <Layout><ModuleLocked module={module || (anyOf ? anyOf[0] : '')} available={ent?.modules || []} /></Layout>;
  }
  // Permission gate for sub-modules that SHARE a plan module: Insurance vs
  // Licence both live in mod_insurance_licenses, Real Estate vs Agreements both
  // in mod_real_estate — so the module check alone can't tell a Licence Officer
  // apart from an Insurance Officer. Each route names its own view permission and
  // we enforce it here (backend already blocks the data endpoint, but without
  // this the shell/toolbar still render on a direct URL). Built-in admins bypass;
  // everyone else must hold `perm`, fail closed while perms load.
  if (perm) {
    const userStr = localStorage.getItem('user');
    const role = (() => { try { return userStr ? JSON.parse(userStr).role : null; } catch { return null; } })();
    const isBuiltinAdmin = role === 'super_admin' || role === 'tenant_admin';
    if (!isBuiltinAdmin) {
      if (permsLoading) {
        return <Layout><div style={{ padding: '60px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>Loading…</div></Layout>;
      }
      if (!can(perm)) {
        return (
          <Layout>
            <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
              <div style={{
                background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '20px',
                padding: '32px 28px', maxWidth: '480px', textAlign: 'center',
                fontFamily: "'Poppins', sans-serif",
              }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 600, color: '#111827' }}>
                  Access Restricted
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
                  You don't have access to this record type. If you need it, contact
                  your workspace administrator.
                </p>
              </div>
            </div>
          </Layout>
        );
      }
    }
  }
  return <Layout>{children}</Layout>;
};

// Best landing route for the tenant: the dashboard when they have Document
// Processing (it's document-centric), otherwise their first available module.
const MODULE_HOME: Record<string, string> = {
  mod_document_processing: '/documents',
  mod_tasks_compliance: '/tasks',
  mod_real_estate: '/real-estate',
  mod_insurance_licenses: '/insurances',
};

const LandingRedirect = () => {
  const token = localStorage.getItem('token');
  const { ent, hasModule, loading } = useEntitlements();
  const { can, loading: permsLoading } = usePermissions();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (loading || permsLoading) {
    return <div style={{ padding: '60px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>Loading…</div>;
  }
  // super_admin or has doc processing → the dashboard is a good home.
  if (ent?.super_admin || hasModule('mod_document_processing')) {
    return <Navigate to="/dashboard" replace />;
  }
  // For the two plan modules that host two record types each, pick the sub-route
  // the user actually holds the view permission for — otherwise a Licence-only
  // officer whose only module is mod_insurance_licenses would land on Insurances
  // (shared module) and hit Access Restricted. Falls back to the module default.
  const homeFor = (m: string): string => {
    if (m === 'mod_insurance_licenses') return can('insurance.view') ? '/insurances' : (can('license.view') ? '/licenses' : '/insurances');
    if (m === 'mod_real_estate') return can('realestate.view') ? '/real-estate' : (can('agreement.view') ? '/agreements' : '/real-estate');
    return MODULE_HOME[m];
  };
  const first = (ent?.modules || []).find((m) => MODULE_HOME[m]);
  return <Navigate to={first ? homeFor(first) : '/dashboard'} replace />;
};

/** /documents/<id> (old links) -> /process/<id>, the document page. */
const DocumentLinkRedirect: React.FC = () => {
  const { documentId } = useParams();
  return <Navigate to={`/process/${documentId}`} replace />;
};

function App() {
  return (
    <ThemeProvider>
      <OrgFilterProvider>
      <Router>
        <AppTitle />
        <div className="App bg-background-primary text-text-primary min-h-screen transition-colors duration-300">
          {/* Proactive: warns before the deadline. ReadOnlyBanner is the
              reactive half — it only appears once a write has been refused. */}
          <PaymentDueBanner />
          <ReadOnlyBanner />
          <React.Suspense fallback={<div style={{padding:24}}>Loading…</div>}>
          <Routes>
            {/* Unauthenticated routes - no layout shell */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            {/* Emailed password-reset link lands here (audit 1.2) */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* Impersonation entry — consumes a token from the URL fragment (Site Panel) */}
            <Route path="/impersonate" element={<ImpersonateEntry />} />
            <Route path="/pending" element={<PendingApproval />} />

            {/* The party portal. Outside the shell AND outside ProtectedRoute
                on purpose: these pages belong to a vendor, customer or employee
                who is not a user of this application, holds no role, and must
                never be handed the navigation of one. Their session lives under
                a different storage key and their token is refused by every
                other endpoint (app.auth.auth.verify_token). */}
            {/* Portal parties sign in on the main /login page now (it falls back
                to the portal when the staff sign-in does not match). The old
                address still works for links already sent out. */}
            <Route path="/portal/login" element={<Navigate to="/login" replace />} />
            <Route path="/portal/set-password" element={<PortalLogin />} />
            <Route path="/portal" element={<PortalPage />} />
            {/* A task e-mailed to someone outside: one-use magic link, no login. */}
            <Route path="/t/:token" element={<GuestTaskPage />} />
            {/* External dossier client: magic link to upload the documents asked for. */}
            <Route path="/d/:token" element={<DossierPortalPage />} />

            {/* Pages wrapped in the shared PaperBridge shell */}
            {/* Grouped pages (tabs). Each tab keeps its old access rule; the old
                addresses above/below redirect to the matching tab. */}
            <Route path="/records" element={<ProtectedLayoutRoute><RecordsHub /></ProtectedLayoutRoute>} />
            <Route path="/organization" element={<ProtectedLayoutRoute><OrganizationHub /></ProtectedLayoutRoute>} />
            <Route path="/automation" element={<ProtectedLayoutRoute><AutomationHub /></ProtectedLayoutRoute>} />
            <Route path="/catalog" element={<ProtectedLayoutRoute><CatalogPage /></ProtectedLayoutRoute>} />
            <Route path="/catalog/:id" element={<ProtectedLayoutRoute><RecordDetailPage /></ProtectedLayoutRoute>} />
            <Route path="/dossiers" element={<ProtectedLayoutRoute><DossiersListPage /></ProtectedLayoutRoute>} />
            <Route path="/dossiers/:id" element={<ProtectedLayoutRoute><DossierDetailPage /></ProtectedLayoutRoute>} />
            <Route path="/submissions" element={<ProtectedLayoutRoute><SubmissionsListPage /></ProtectedLayoutRoute>} />
            <Route path="/submissions/:id" element={<ProtectedLayoutRoute><SubmissionDetailPage /></ProtectedLayoutRoute>} />
            <Route path="/record-types" element={<ProtectedLayoutRoute><RecordTypeBuilderPage /></ProtectedLayoutRoute>} />
            <Route path="/work-types" element={<ProtectedLayoutRoute><WorkTypeBuilderPage /></ProtectedLayoutRoute>} />
            <Route path="/engagements" element={<ProtectedLayoutRoute><EngagementsPage /></ProtectedLayoutRoute>} />
            <Route path="/platform" element={<SuperAdminLayoutRoute what="Platform settings"><PlatformHub /></SuperAdminLayoutRoute>} />
            <Route path="/dashboard" element={<ProtectedLayoutRoute><DashboardHub /></ProtectedLayoutRoute>} />
            <Route path="/documents" element={<ModuleLayoutRoute module="mod_document_processing"><DocumentsHub /></ModuleLayoutRoute>} />
            <Route path="/insights" element={<ToSection to="/dashboard" section="insights" />} />
            <Route path="/auditor" element={<ModuleLayoutRoute module="mod_document_processing"><UserDashboard /></ModuleLayoutRoute>} />
            <Route path="/tasks" element={<ModuleLayoutRoute module="mod_tasks_compliance"><TasksHub /></ModuleLayoutRoute>} />
            {/* Approvals, Arbitrator and Acknowledgements are one page with three tabs.
                The old addresses (still used by notifications and bookmarks) open the right tab. */}
            <Route path="/approvals" element={<ProtectedLayoutRoute><ApprovalsPage /></ProtectedLayoutRoute>} />
            <Route path="/workflow-tasks" element={<Navigate to="/approvals?tab=approvals" replace />} />
            <Route path="/compliance" element={<ModuleLayoutRoute module="mod_tasks_compliance"><CompliancePage /></ModuleLayoutRoute>} />
            {/* Timesheets are for every tenant — no module gate. */}
            <Route path="/timesheets" element={<ModuleLayoutRoute><TimesheetHub /></ModuleLayoutRoute>} />
            <Route path="/timesheets/team" element={<ToSection to="/timesheets" section="team" />} />
            {/* Report route must precede /:id so "report" is not captured as an id. */}
            <Route path="/timesheets/report" element={<ModuleLayoutRoute><TimesheetsReportPage /></ModuleLayoutRoute>} />
            <Route path="/timesheets/:id" element={<ModuleLayoutRoute><TimesheetDetailPage /></ModuleLayoutRoute>} />
            <Route path="/master-data" element={<ModuleLayoutRoute module="mod_document_processing"><MasterDataPage /></ModuleLayoutRoute>} />
            {/* Super-admin-only: see SuperAdminLayoutRoute above. */}
            <Route path="/field-definitions" element={<ToSection to="/platform" section="fields" />} />
            <Route path="/document-field-config" element={<ToSection to="/platform" section="doc-types" />} />
            {/* Forensic Detection Layer — see FORENSIC_DETECTION_DESIGN.md */}
            <Route path="/forensic-config" element={<ToSection to="/platform" section="forensic" />} />
            {/* Phase 14 Step 1 — User Management page */}
            <Route path="/users" element={<AdminLayoutRoute perm="manage_users"><UsersPage /></AdminLayoutRoute>} />
            <Route path="/recycle-bin" element={<AdminLayoutRoute perm="delete_document"><RecycleBinPage /></AdminLayoutRoute>} />

            {/* Phase 14 Step 2 — Administration pages */}
            <Route path="/roles" element={<ToSection to="/platform" section="roles" />} />
            <Route path="/organizations-admin" element={<AdminLayoutRoute perm="manage_organizations"><OrganizationsAdminPage /></AdminLayoutRoute>} />
            {/* Automation Rule Studio (P3) — admins or a role with rules.manage. */}
            <Route path="/rule-studio" element={<ToSection to="/automation" section="rules" />} />
            {/* Arbitrator — department-routing triage (perm routing.arbitrate). */}
            <Route path="/arbitrator" element={<Navigate to="/approvals?tab=arbitrator" replace />} />
            {/* External Master — vendors, customers and employees, and where
                their documents go. Gated on routing.arbitrate: maintaining it IS
                the arbitrator's job, and rules.manage is hidden from the role
                editor so no role could ever be granted it. */}
            <Route path="/external-master" element={<AdminLayoutRoute perm="routing.arbitrate"><ExternalMasterPage /></AdminLayoutRoute>} />
            {/* Old path kept so an open tab or a bookmark still lands somewhere. */}
            <Route path="/vendor-master" element={<AdminLayoutRoute perm="routing.arbitrate"><ExternalMasterPage /></AdminLayoutRoute>} />
            {/* Tax & Tolerances — editable tax reference data (Level 2). */}
            <Route path="/tax-config" element={<Navigate to="/automation?section=rules&view=types" replace />} />
            {/* ERP lookup connectors (enrichment). */}
            <Route path="/connectors" element={<Navigate to="/configurations?tab=erp" replace />} />
            {/* Workflow Studio (P0–P5) — dark behind WORKFLOW_ENGINE_ENABLED. */}
            <Route path="/workflow-studio" element={<ToSection to="/automation" section="workflows" />} />
            <Route path="/departments-admin" element={<AdminLayoutRoute perm="manage_departments"><DepartmentsAdminPage /></AdminLayoutRoute>} />
            <Route path="/whatsapp-senders" element={<SuperAdminLayoutRoute what="WhatsApp Senders"><WhatsAppSendersPage /></SuperAdminLayoutRoute>} />
            <Route path="/sla-dashboard" element={<ToSection to="/dashboard" section="team-performance" />} />
            {/* Recipe layer — CPU-vs-GPU savings + per-recipe health (admin-only). */}
            {/* Site Panel (super_admin) only; controllers / tenant-admins no longer see it. */}
            <Route path="/recipe-stats" element={<ToSection to="/platform" section="recipes" />} />

            {/* Configurations — SLA, Storage, Zoho, Tally all live under one
                tabbed page. The legacy routes below redirect here so old links
                and bookmarks keep working. */}
            <Route path="/configurations" element={<AdminLayoutRoute><ConfigurationsPage /></AdminLayoutRoute>} />
            <Route path="/sla-config" element={<Navigate to="/configurations?tab=sla" replace />} />
            <Route path="/storage-config" element={<Navigate to="/configurations?tab=storage" replace />} />
            <Route path="/workflow-settings" element={<Navigate to="/configurations?tab=approval" replace />} />
            <Route path="/integrations/zoho" element={<Navigate to="/configurations?tab=zoho" replace />} />
            <Route path="/integrations/tally" element={<Navigate to="/configurations?tab=tally" replace />} />
            {/* Subscription & Plan — what the tenant bought, used, and owes */}
            <Route path="/subscription" element={<AdminLayoutRoute><SubscriptionPage /></AdminLayoutRoute>} />
            {/* Phase 17.1b — Insurance Management */}
            <Route path="/insurances" element={<ModuleLayoutRoute module="mod_insurance_licenses" perm="insurance.view"><InsurancesPage /></ModuleLayoutRoute>} />
            <Route path="/insurances/:id" element={<ModuleLayoutRoute module="mod_insurance_licenses" perm="insurance.view"><InsuranceDetailPage /></ModuleLayoutRoute>} />
            <Route path="/insurances-report" element={<ModuleLayoutRoute module="mod_insurance_licenses" perm="insurance.view"><InsuranceReportPage /></ModuleLayoutRoute>} />
            <Route path="/licenses" element={<ModuleLayoutRoute module="mod_insurance_licenses" perm="license.view"><LicensesPage /></ModuleLayoutRoute>} />
            <Route path="/licenses/:id" element={<ModuleLayoutRoute module="mod_insurance_licenses" perm="license.view"><LicenseDetailPage /></ModuleLayoutRoute>} />
            <Route path="/licenses-report" element={<ModuleLayoutRoute module="mod_insurance_licenses" perm="license.view"><LicenseReportPage /></ModuleLayoutRoute>} />
            <Route path="/agreements" element={<ModuleLayoutRoute module="mod_real_estate" perm="agreement.view"><AgreementsPage /></ModuleLayoutRoute>} />
            <Route path="/agreements/:id" element={<ModuleLayoutRoute module="mod_real_estate" perm="agreement.view"><AgreementDetailPage /></ModuleLayoutRoute>} />
            <Route path="/agreements-report" element={<ModuleLayoutRoute module="mod_real_estate" perm="agreement.view"><AgreementReportPage /></ModuleLayoutRoute>} />
            {/* Phase 17.2b — Real Estate & Lease Documents */}
            <Route path="/real-estate" element={<ModuleLayoutRoute module="mod_real_estate" perm="realestate.view"><RealEstatePage /></ModuleLayoutRoute>} />
            <Route path="/real-estate/:id" element={<ModuleLayoutRoute module="mod_real_estate" perm="realestate.view"><PropertyDetailPage /></ModuleLayoutRoute>} />
            <Route path="/records/pending-approvals" element={<Navigate to="/approvals?tab=acknowledgements" replace />} />
            <Route path="/property-locations" element={<ModuleLayoutRoute module="mod_real_estate"><PropertyLocationsPage /></ModuleLayoutRoute>} />
            <Route path="/real-estate-report" element={<ModuleLayoutRoute module="mod_real_estate" perm="realestate.view"><RealEstateReportPage /></ModuleLayoutRoute>} />
            <Route path="/documents-report" element={<ModuleLayoutRoute module="mod_document_processing"><DocumentsReportPage /></ModuleLayoutRoute>} />
            <Route path="/tasks-report" element={<ModuleLayoutRoute module="mod_tasks_compliance"><TasksReportPage /></ModuleLayoutRoute>} />
            <Route path="/compliance-report" element={<ModuleLayoutRoute module="mod_tasks_compliance"><ComplianceReportPage /></ModuleLayoutRoute>} />
            {/* Phase 18 — Document Search */}
            <Route path="/search" element={<ModuleLayoutRoute module="mod_document_processing"><DocumentSearchPage /></ModuleLayoutRoute>} />

            {/* Task detail page wrapped in shared shell (Phase 6) */}
            <Route path="/tasks/:taskId" element={<ModuleLayoutRoute module="mod_tasks_compliance"><TaskDetailPage /></ModuleLayoutRoute>} />

            {/* Phase 14 Step 3 — /admin retired. Any deep links land on the unified /dashboard. */}
            <Route path="/admin" element={<Navigate to="/dashboard" replace />} />

            {/* Pages kept on legacy layout until their respective redesign phase */}
            <Route path="/process/:documentId" element={<ProtectedRoute><DocumentProcessor /></ProtectedRoute>} />
            {/* Older notifications and task links point at /documents/<id>; the page is /process/<id>. */}
            <Route path="/documents/:documentId" element={<DocumentLinkRedirect />} />
            {/* Zoho Review & Post screen — side-by-side source PDF +
                editable Zoho payload. Uses ProtectedLayoutRoute so the
                Sidebar+Header chrome wraps it; the page deliberately
                takes the full available height. */}
            <Route path="/documents/:documentId/zoho-review" element={<ProtectedLayoutRoute><ZohoReviewPage /></ProtectedLayoutRoute>} />

            <Route path="/" element={<LandingRedirect />} />
            {/* Unknown URL: signed-in users go to their home (same logic as "/"),
                everyone else to /login -- instead of a blank page. */}
            <Route path="*" element={<LandingRedirect />} />
          </Routes>
          </React.Suspense>
        </div>
      </Router>
      </OrgFilterProvider>
    </ThemeProvider>
  );
}

export default App;
